import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { UserService } from 'src/app/core/services/user.service';
import { environment } from 'src/environments/environment';
import {
  PersonalDetailsFormConfig,
  PersonalDetailsValue,
} from '../personal-details-form/personal-details-form.component';

export interface PersonalDetailsDialogData {
  userId: string;
  title?: string; // i18n key
  description?: string; // i18n key
  config: PersonalDetailsFormConfig;
}

export interface PersonalDetailsDialogResult {
  updated: boolean;
  values?: PersonalDetailsValue;
}

/**
 * Standalone "amend your details" modal hosting <app-personal-details-form>.
 * On save it persists the collected fields via POST /profile and closes with
 * { updated: true }. No leak/SERPRO check — that lives in the mismatch modal.
 */
@Component({
  selector: 'app-personal-details-modal',
  templateUrl: './personal-details-modal.component.html',
  styleUrls: ['./personal-details-modal.component.scss'],
})
export class PersonalDetailsModalComponent {
  saving = false;
  error = '';

  private value: PersonalDetailsValue = {};
  private valid = false;

  constructor(
    public dialogRef: MatDialogRef<PersonalDetailsModalComponent, PersonalDetailsDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: PersonalDetailsDialogData,
    private http: HttpClient,
    private userService: UserService
  ) {}

  get canSave(): boolean {
    return this.valid && !this.saving;
  }

  onValueChange(value: PersonalDetailsValue): void {
    this.value = value;
  }

  onValidityChange(valid: boolean): void {
    this.valid = valid;
  }

  save(): void {
    if (!this.canSave) return;
    this.saving = true;
    this.error = '';
    void this.persist();
  }

  cancel(): void {
    this.dialogRef.close({ updated: false });
  }

  private async persist(): Promise<void> {
    try {
      const token = (await this.userService.supabase.auth.getSession()).data.session
        ?.access_token;

      await firstValueFrom(
        this.http.post(environment.lambdaUrl + '/profile', this.buildBody(), {
          headers: { Authorization: `Bearer ${token}` },
        })
      );

      this.dialogRef.close({ updated: true, values: this.value });
    } catch (err) {
      console.error('[PersonalDetailsModal] Error updating profile:', err);
      this.error = 'Error updating information. Please try again.';
      this.saving = false;
    }
  }

  private buildBody(): Record<string, unknown> {
    const body: Record<string, unknown> = { user_id: this.data.userId };
    if (this.value.legalName !== undefined) body['legal_name'] = this.value.legalName;
    if (this.value.dateOfBirth !== undefined) body['date_of_birth'] = this.value.dateOfBirth;
    if (this.value.cpf !== undefined) body['id_document_value'] = this.value.cpf;
    return body;
  }
}
